import { extractUrls, type UrlEntity } from '../domain/note'
import { removeKnownTrackingParameters } from '../utils/urlTracking'
import { hasSpecificLinkImage, linkDetailsLoading } from './linkMetadata'

/**
 * How a link is shown inside a note. The display follows where the link sits, not a setting, and
 * never changes the Markdown. See docs/link-notes/PLAN.md.
 *
 *  - card: the link is the note's first block and the note's only link, and the page has a picture
 *  - chip / row / url: later phases (a link inside a sentence, one of several, or chosen by the owner)
 */
export type LinkDisplay = 'card' | 'row' | 'chip' | 'url'

/** What the display switch offers: a pill, a row, or the large preview (the card above the note, or a sheet). */
export type LinkDisplayChoice = 'chip' | 'row' | 'preview'

/** The switch's options, in order, with the words used for labels and tooltips. One list, so every surface offers the same. */
export const LINK_DISPLAY_OPTIONS: ReadonlyArray<readonly [LinkDisplayChoice, string]> = [['chip', 'Pill'], ['row', 'Row'], ['preview', 'Preview']]

const OWN_LINE_LINK = /^(?:\[(?:\\.|[^\]\\\n])+\]\((https?:\/\/[^\s<>()[\]{}"']+)(?:\s+["'][^"']*["'])?\)|<(https?:\/\/[^\s<>()[\]{}"']+)>|(https?:\/\/[^\s<>()[\]{}"']+))[.,;:!?]*$/i

/** One comparable key for a link target, so the Markdown, the DOM and the URL entity agree. */
export function linkTargetKey(href: string): string {
  try {
    const parsed = new URL(removeKnownTrackingParameters(href.trim()))
    parsed.hash = ''
    return parsed.toString()
  } catch {
    return href.trim()
  }
}

function entityKeys(url: UrlEntity): string[] {
  return [url.originalUrl, url.canonicalUrl ?? ''].filter(Boolean).map(linkTargetKey)
}

/**
 * The link to show as a card above the editor: the note's only link, sitting alone as its first
 * block. Words may follow it (they flow below the card); words before it keep today's layout until
 * links render in place. Returns null otherwise.
 */
export function leadingLinkCard(body: string, urls: readonly UrlEntity[]): UrlEntity | null {
  if (urls.length !== 1 || extractUrls(body).length !== 1 || urls[0]!.display === 'chip' || urls[0]!.display === 'row') return null
  // By default a large preview is for a link with a picture. Without one (the page has none, or refuses it) a row is the better
  // default; while the first read is still under way the picture is not known yet, so the card waits for it. A person who chose
  // the large preview (display 'preview') gets it either way.
  if (urls[0]!.display !== 'preview' && !linkDetailsLoading(urls[0]!) && !hasSpecificLinkImage(urls[0]!, urls[0]!.imageUrl)) return null
  const lines = body.replace(/^\s*\n/, '').split(/\r?\n/)
  const first = lines[0]?.trim() ?? ''
  if (!first || (lines[1] ?? '').trim() !== '') return null
  const match = OWN_LINE_LINK.exec(first)
  const href = match?.[1] ?? match?.[2] ?? match?.[3]
  if (!href) return null
  const key = linkTargetKey(href)
  return entityKeys(urls[0]!).includes(key) ? urls[0]! : null
}
